import { Controller } from '@hotwired/stimulus'

interface FilterOption {
  value: string
  label: string
  type: string
}

interface MultiselectChangeEvent extends CustomEvent {
  detail: {
    selectId: string
    value: string
    action: string
  }
}

interface TurboSubmitEvent extends CustomEvent {
  detail: {
    formSubmission: {
      result: {
        scrollToTop: boolean
      }
    }
  }
}

interface TurboFrameEvent extends CustomEvent {
  detail: Record<string, unknown>
}

interface TomSelectOption {
  text: string
}

interface TomSelectInstance {
  items: string[]
  options: Record<string, TomSelectOption>
  removeItem: (value: string, silent: boolean) => void
  clear: (silent: boolean) => void
  setTextboxValue: (value: string) => void
  refreshOptions: (triggerDropdown: boolean) => void
  settings: Record<string, unknown>
}

interface TomSelectInfo {
  instance: TomSelectInstance
  label: string
}

export default class extends Controller {
  static targets = ['resetButton', 'chipContainer', 'form', 'showMoreButton']

  declare readonly resetButtonTarget: HTMLButtonElement
  declare readonly chipContainerTarget: HTMLDivElement
  declare readonly formTarget: HTMLFormElement
  declare readonly showMoreButtonTarget: HTMLButtonElement
  declare readonly hasFormTarget: boolean
  declare readonly hasResetButtonTarget: boolean
  declare readonly hasChipContainerTarget: boolean
  declare readonly hasShowMoreButtonTarget: boolean

  private readonly tomSelectInstances: Map<string, TomSelectInfo> = new Map()
  private isProcessing = false
  private submissionInProgress = false
  private formElement: HTMLFormElement | null = null
  private formSubmissionTimeout: ReturnType<typeof setTimeout> | null = null
  private savedChipState: Array<{ value: string, label: string, type: string }> = []
  private showAllChips = false
  private readonly maxVisibleChips = 5
  private searchInputTimer: ReturnType<typeof setTimeout> | null = null

  connect (): void {
    console.log('Advanced Filter controller connected')

    if (this.hasFormTarget) {
      this.formElement = this.formTarget
    } else {
      console.warn('Missing form target - checking if controller element is a form')
      if (this.element instanceof HTMLFormElement) {
        this.formElement = this.element
      } else {
        console.error('No form target found and controller element is not a form')
        return
      }
    }

    if (!this.hasChipContainerTarget) {
      console.warn('Missing chipContainer target')
    }

    if (!this.hasResetButtonTarget) {
      console.warn('Missing resetButton target')
    }

    if (!this.hasShowMoreButtonTarget) {
      console.warn('Missing showMoreButton target')
    }

    this.registerTomSelectInstances()

    this.initializeChips()

    this.element.addEventListener('multiselect:change',
      ((event: MultiselectChangeEvent) => this.handleMultiselectChange(event)) as EventListener
    )

    document.addEventListener('turbo:before-frame-render',
      ((event: TurboFrameEvent) => {
        this.savedChipState = this.getCurrentChipState()
        this.submissionInProgress = true
      }) as EventListener
    )

    document.addEventListener('turbo:frame-render', ((event: TurboFrameEvent) => {
      setTimeout(() => {
        const currentChipCount = this.chipContainerTarget?.children.length ?? 0
        if (currentChipCount < this.savedChipState.length) {
          this.restoreChipState(this.savedChipState)
        }

        const textInputs = this.element.querySelectorAll('input[type="text"]')
        textInputs.forEach(input => {
          (input as HTMLInputElement).value = ''
        })

        const nativeSelects = this.element.querySelectorAll('select')
        nativeSelects.forEach(select => {
          (select).selectedIndex = 0
        })

        if (this.chipContainerTarget !== null && this.chipContainerTarget !== undefined) {
          this.chipContainerTarget.innerHTML = ''
        }

        this.tomSelectInstances.forEach(({ instance }) => {
          instance.clear(true)
        })

        this.updateChipsVisibility()

        this.submissionInProgress = false
      }, 10)
    }) as EventListener)

    if (this.formElement !== null) {
      this.formElement.addEventListener('turbo:submit-start',
        ((event: TurboSubmitEvent) => {
          try {
            this.savedChipState = this.getCurrentChipState()

            if (event.detail?.formSubmission?.result !== undefined) {
              event.detail.formSubmission.result.scrollToTop = false
            }
          } catch (error) {
            console.warn('Could not set scrollToTop property:', error)
          }
        }) as EventListener
      )

      this.formElement.addEventListener('turbo:submit-end',
        (() => {
          setTimeout(() => {
            this.restoreChipState(this.savedChipState)
            this.updateChipsVisibility()
            this.submissionInProgress = false
          }, 10)
        }) as EventListener
      )
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        setTimeout(() => {
          if (!this.submissionInProgress) {
            this.refreshChips()
            this.updateChipsVisibility()
          }
        }, 300)
      }
    })
  }

  toggleShowMore (): void {
    this.showAllChips = !this.showAllChips
    this.updateChipsVisibility()
  }

  updateChipsVisibility (): void {
    if (!this.hasChipContainerTarget || !this.hasShowMoreButtonTarget) {
      return
    }

    const chips = Array.from(this.chipContainerTarget.children) as HTMLElement[]
    const totalChips = chips.length

    if (totalChips <= this.maxVisibleChips) {
      this.showMoreButtonTarget.classList.add('hidden')

      chips.forEach(chip => {
        chip.classList.remove('hidden')
      })
      return
    }

    this.showMoreButtonTarget.classList.remove('hidden')

    const buttonText = this.showAllChips
      ? 'Show less'
      : `Show more (${totalChips - this.maxVisibleChips})`

    this.showMoreButtonTarget.textContent = buttonText

    chips.forEach((chip, index) => {
      if (index < this.maxVisibleChips || this.showAllChips) {
        chip.classList.remove('hidden')
      } else {
        chip.classList.add('hidden')
      }
    })
  }

  private getCurrentChipState (): Array<{ value: string, label: string, type: string }> {
    if (!this.hasChipContainerTarget) {
      return []
    }

    const chips = this.chipContainerTarget.querySelectorAll('[data-value][data-type]')
    return Array.from(chips).map(chip => ({
      value: chip.getAttribute('data-value') ?? '',
      label: chip.getAttribute('data-label') ?? '',
      type: chip.getAttribute('data-type') ?? ''
    })).filter(chip => chip.value !== '' && chip.type !== '')
  }

  private restoreChipState (chipState: Array<{ value: string, label: string, type: string }>): void {
    if (!this.hasChipContainerTarget) {
      return
    }

    const existingChips = new Set<string>()

    this.chipContainerTarget.querySelectorAll('[data-value][data-type]').forEach(chip => {
      const value = chip.getAttribute('data-value')
      const type = chip.getAttribute('data-type')
      if (value !== null && type !== null) {
        existingChips.add(`${type}:${value}`)
      }
    })

    chipState.forEach(chip => {
      const chipKey = `${chip.type}:${chip.value}`
      if (!existingChips.has(chipKey)) {
        this.createChip(chip)
      }
    })

    this.updateChipsVisibility()
  }

  registerTomSelectInstances (): void {
    const tomSelectElements = this.element.querySelectorAll('.tomselected')

    tomSelectElements.forEach(element => {
      if (!(element instanceof HTMLElement)) {
        return
      }

      const id = element.id
      if (id === '') {
        return
      }

      const labelElement = element.closest('.relative')?.querySelector('span')
      const labelText = labelElement?.textContent ?? id

      const tomSelect = (element as any).tomSelect
      if (tomSelect !== undefined) {
        this.tomSelectInstances.set(id, {
          instance: tomSelect,
          label: labelText
        })
      } else {
        const checkInterval = setInterval(() => {
          const updatedTomSelect = (element as any).tomSelect
          if (updatedTomSelect !== undefined) {
            this.tomSelectInstances.set(id, {
              instance: updatedTomSelect,
              label: labelText
            })
            clearInterval(checkInterval)
          }
        }, 100)
      }
    })
  }

  initializeChips (): void {
    if (!this.hasChipContainerTarget) {
      return
    }

    setTimeout(() => {
      this.tomSelectInstances.forEach(({ instance, label }, selectId) => {
        if (instance?.items !== undefined) {
          const selectedItems = instance.items

          selectedItems.forEach((value: string) => {
            const option = instance.options[value]
            if (option !== undefined) {
              this.createChip({
                value,
                label: option.text ?? value,
                type: selectId
              }, true)
            }
          })
        }
      })

      this.updateChipsVisibility()
    }, 300)
  }

  refreshChips (): void {
    if (this.submissionInProgress || !this.hasChipContainerTarget) {
      return
    }

    const existingChips = this.getCurrentChipState()
    const existingChipsMap = new Map<string, { value: string, label: string, type: string }>()
    existingChips.forEach(chip => {
      existingChipsMap.set(`${chip.type}:${chip.value}`, chip)
    })

    const currentSelections: Array<{ value: string, label: string, type: string }> = []
    this.tomSelectInstances.forEach(({ instance, label }, selectId) => {
      if (instance?.items !== undefined) {
        instance.items.forEach((value: string) => {
          const option = instance.options[value]
          if (option !== undefined) {
            currentSelections.push({
              value,
              label: option.text ?? value,
              type: selectId
            })
          }
        })
      }
    })

    const currentSelectionsMap = new Map<string, boolean>()
    currentSelections.forEach(selection => {
      currentSelectionsMap.set(`${selection.type}:${selection.value}`, true)
    })

    existingChips.forEach(chip => {
      const chipKey = `${chip.type}:${chip.value}`
      if (!currentSelectionsMap.has(chipKey)) {
        this.removeChipByValue(chip.value, chip.type, true)
      }
    })

    currentSelections.forEach(selection => {
      const selectionKey = `${selection.type}:${selection.value}`
      if (!existingChipsMap.has(selectionKey)) {
        this.createChip(selection, true)
      }
    })

    this.updateChipsVisibility()
  }

  handleMultiselectChange (event: MultiselectChangeEvent): void {
    const { selectId, value, action } = event.detail

    if (this.isProcessing) {
      return
    }

    this.isProcessing = true

    if (action === 'add') {
      const info = this.tomSelectInstances.get(selectId)
      if (info?.instance !== undefined) {
        const option = info.instance.options[value]
        if (option !== undefined) {
          this.createChip({
            value,
            label: option.text ?? value,
            type: selectId
          }, false)

          this.savedChipState = this.getCurrentChipState()

          this.updateChipsVisibility()
        }
      }
    } else if (action === 'remove') {
      this.removeChipByValue(value, selectId)

      this.savedChipState = this.getCurrentChipState()

      this.updateChipsVisibility()
    }

    requestAnimationFrame(() => {
      this.submitFormSmoothly(() => {
        setTimeout(() => {
          this.isProcessing = false
        }, 100)
      })
    })
  }

  inputSearch (event: Event): void {
    if (this.searchInputTimer !== null) {
      clearTimeout(this.searchInputTimer)
    }

    this.searchInputTimer = setTimeout(() => {
      if (this.isProcessing) return

      this.isProcessing = true

      requestAnimationFrame(() => {
        this.submitFormSmoothly(() => {
          setTimeout(() => {
            this.isProcessing = false
          }, 100)
        })
      })
    }, 300)
  }

  select (event: Event): void {
    if (this.isProcessing) {
      return
    }

    this.isProcessing = true

    if (!(event.target instanceof HTMLSelectElement)) {
      this.isProcessing = false
      return
    }

    const select = event.target
    const id = select.id

    if (id === '') {
      this.isProcessing = false
      return
    }

    const selectedOptions = Array.from(select.selectedOptions)

    this.removeChipsBySelectId(id, true)

    selectedOptions.forEach(option => {
      this.createChip({
        value: option.value,
        label: option.textContent ?? option.value,
        type: id
      }, false)
    })

    this.savedChipState = this.getCurrentChipState()

    this.updateChipsVisibility()

    requestAnimationFrame(() => {
      this.submitFormSmoothly(() => {
        setTimeout(() => {
          this.isProcessing = false
        }, 100)
      })
    })
  }

  remove (event: Event): void {
    if (this.isProcessing) {
      return
    }

    this.isProcessing = true
    event.preventDefault()
    event.stopPropagation()

    if (!(event.currentTarget instanceof HTMLElement)) {
      this.isProcessing = false
      return
    }

    const chipElement = event.currentTarget.closest('[data-value]')
    if (!(chipElement instanceof HTMLElement)) {
      this.isProcessing = false
      return
    }

    const value = chipElement.getAttribute('data-value')
    const type = chipElement.getAttribute('data-type')

    if (value !== null && type !== null && value !== '' && type !== '') {
      const info = this.tomSelectInstances.get(type)

      if (info?.instance !== undefined) {
        this.savedChipState = this.getCurrentChipState().filter(
          chip => !(chip.value === value && chip.type === type)
        )

        info.instance.removeItem(value, true)
      }

      requestAnimationFrame(() => {
        chipElement.remove()

        this.updateChipsVisibility()

        this.submitFormSmoothly(() => {
          setTimeout(() => {
            this.isProcessing = false
          }, 100)
        })
      })
    } else {
      this.isProcessing = false
    }
  }

  resetFilters (event?: Event): void {
    if (this.isProcessing) {
      return
    }

    if (event !== undefined) {
      event.preventDefault()
      event.stopPropagation()
    }

    this.isProcessing = true

    this.savedChipState = []

    if (this.hasChipContainerTarget) {
      this.chipContainerTarget.innerHTML = ''
    }

    this.tomSelectInstances.forEach(({ instance }) => {
      if (instance !== undefined) {
        const itemsToRemove = [...instance.items]

        const originalSettings = instance.settings
        instance.settings.onChange = () => { /* no-op */ }

        itemsToRemove.forEach(item => {
          instance.removeItem(String(item), true)
        })

        instance.clear(true)

        instance.setTextboxValue('')
        instance.refreshOptions(false)

        instance.settings = originalSettings
      }
    })

    const searchInput = this.element.querySelector('input[placeholder="Search templates"]')
    if (searchInput instanceof HTMLInputElement) {
      searchInput.value = ''
    }

    this.showAllChips = false
    this.updateChipsVisibility()

    requestAnimationFrame(() => {
      const form = this.formElement
      if (form !== null) {
        const resetParam = new URLSearchParams(window.location.search)
        resetParam.set('reset_filters', '1')

        const url = new URL(form.action)
        url.search = resetParam.toString()

        const originalAction = form.action
        form.action = url.toString()

        try {
          form.requestSubmit()
        } catch (error) {
          form.submit()
        }

        setTimeout(() => {
          form.action = originalAction
        }, 100)

        setTimeout(() => {
          this.isProcessing = false
        }, 400)
      } else {
        this.isProcessing = false
      }
    })
  }

  private createChip (option: FilterOption, checkIfExists = true): void {
    if (!this.hasChipContainerTarget) {
      return
    }

    const chipContainer = this.chipContainerTarget

    if (checkIfExists) {
      const existingChip = chipContainer.querySelector(`[data-value="${option.value}"][data-type="${option.type}"]`)
      if (existingChip !== null) {
        return
      }
    }

    const chip = document.createElement('div')
    chip.className = 'flex items-center py-1 pl-3 pr-2 space-x-1 border rounded-full shadow-low text-primary border-subtle bg-raised whitespace-nowrap'
    chip.setAttribute('data-value', option.value)
    chip.setAttribute('data-label', option.label)
    chip.setAttribute('data-type', option.type)

    chip.innerHTML = `
      <span class="text-sm font-medium leading-6">${option.label}</span>
      <div class="remove" data-action="click->risk-template-filter#remove" data-cy="chips-remove">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5 text-secondary cursor-pointer">
          <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
        </svg>
      </div>
    `

    chipContainer.appendChild(chip)

    this.updateChipsVisibility()
  }

  private removeChipsBySelectId (selectId: string, silent = false): void {
    if (!this.hasChipContainerTarget) {
      return
    }

    const chipContainer = this.chipContainerTarget
    const chips = chipContainer.querySelectorAll(`[data-type="${selectId}"]`)

    if (!silent) {
      this.savedChipState = this.getCurrentChipState().filter(
        chip => chip.type !== selectId
      )
    }

    chips.forEach(chip => {
      if (chip instanceof HTMLElement) {
        chip.remove()
      }
    })

    this.updateChipsVisibility()
  }

  private removeChipByValue (value: string, type: string, silent = false): void {
    if (!this.hasChipContainerTarget) {
      return
    }

    const chipContainer = this.chipContainerTarget
    const chip = chipContainer.querySelector(`[data-value="${value}"][data-type="${type}"]`)

    if (!silent && chip instanceof HTMLElement) {
      this.savedChipState = this.getCurrentChipState().filter(
        chip => !(chip.value === value && chip.type === type)
      )
    }

    if (chip instanceof HTMLElement) {
      chip.remove()
    }

    this.updateChipsVisibility()
  }

  private submitFormSmoothly (callback?: () => void): void {
    const form = this.formElement
    if (form !== null) {
      if (this.formSubmissionTimeout !== null) {
        clearTimeout(this.formSubmissionTimeout)
      }

      this.formSubmissionTimeout = setTimeout(() => {
        const frameElement = form.closest('turbo-frame')
        const frameId = frameElement?.id ?? ''

        if (frameId !== '') {
          const formData = new FormData(form)

          fetch(form.action, {
            method: form.method.toUpperCase() === 'GET' ? 'GET' : 'POST',
            body: formData,
            headers: {
              Accept: 'text/html',
              'Turbo-Frame': frameId
            }
          }).then(() => {
            setTimeout(() => {
              if (this.hasChipContainerTarget) {
                const currentChipCount = this.chipContainerTarget.children.length
                if (currentChipCount < this.savedChipState.length) {
                  this.restoreChipState(this.savedChipState)
                }

                this.updateChipsVisibility()
              }

              if (callback !== undefined) {
                callback()
              }
            }, 20)
          }).catch(() => {
            if (callback !== undefined) {
              callback()
            }
          })
        } else {
          try {
            form.requestSubmit()
          } catch (error) {
            form.submit()
          }
          if (callback !== undefined) {
            setTimeout(callback, 300)
          }
        }
      }, 50)
    } else if (callback !== undefined) {
      callback()
    }
  }
}
